import React, { useCallback, useEffect, useMemo } from 'react'
import classnames from 'classnames'
import { Link } from 'react-router-dom'
import { Col, Form, InputGroup, Row, Table, Alert } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'
import { useGhgCalculatorFormContext } from './use-ghg-calculator-form'
import { FormValuesWatcher } from './FormValuesWatcher'
import * as yup from 'yup'
import { DateInput } from './base/form/DateInput'
import { NumberField } from './base/form/NumberField'
import { useYupValidationResolver } from 'hooks/use-yup-validation-resolver'
import {
  yupNullableMinZeroDecimal,
  yupNullableMinZeroInteger,
  yupPositiveInteger,
  yupSeasonYear,
  requiredMsg,
} from 'utils/validation'
import Reference from 'yup/lib/Reference'
import { yupDateTransform } from 'utils/date'
import { GhgCalculatorNavigation } from './GhgCalculatorNavigation'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import { safeSum } from '../utils/formatting'
import { FarmDetailState } from 'components/ghg-calculator.state'
import Tippy from '@tippyjs/react'
import NumberFormat from 'react-number-format'

import './GhgFarmDetailStep.scss'
import { TooltipIcon } from './base/TooltipIcon'
import { SEASON_DEFAULT_END_MONTH } from 'utils/season'

const EXISTING_VEGETATION_FIELDS = [
  'area_exotic_forest_existing' as const,
  'area_indigenous_forest_regenerating_existing' as const,
  'area_indigenous_forest_established_existing' as const,
  'area_shrubland_lt30_existing' as const,
  'area_shrubland_gt30_existing' as const,
]

interface FormValues extends FarmDetailState {
  vegetation_existing_total: number
}

const MAX_FARM_NAME_LENGTH = 50

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export const GhgFarmDetailStep = React.memo(() => {
  const {
    farmDetail,
    farmRegionalCouncils,
    farmRegions,
    farmClasses,
    setFarmDetail,
    registerForm,
  } = useGhgCalculatorFormContext()

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        name: yup.string().required(requiredMsg('Name')).max(MAX_FARM_NAME_LENGTH),
        gst_number: yup
          .string()
          .min(9, 'GST number must be 9 digits.')
          .max(9, 'GST number must be 9 digits.')
          .required(requiredMsg('GST number')),
        regional_council: yup
          .number()
          .required(requiredMsg('Regional council'))
          .typeError(requiredMsg('Regional council')),
        region: yup.number().required(requiredMsg('Region')).typeError(requiredMsg('Farm region')),
        classification: yup
          .number()
          .required(requiredMsg('Class'))
          .typeError(requiredMsg('Farm class')),
        area_total: yupPositiveInteger().required(requiredMsg('Total farm area')),
        area_effective: yupPositiveInteger()
          .required(requiredMsg('Effective area'))
          .max(
            yup.ref('area_total') as Reference<number>,
            'Effective area must be less than or equal to Total farm area.'
          ),
        analysis_date: yup
          .date()
          .transform(yupDateTransform)
          .nullable()
          .min(new Date('1900-01-01'), 'Analysis date must be >= 1900-01-01')
          .max(new Date('2100-12-31'), 'Analysis date must be <= 2100-12-31')
          .required(requiredMsg('Analysis date'))
          .typeError('Analysis date is invalid.'),
        season_year: yupSeasonYear(),
        end_month: yupPositiveInteger(),
        weight_non_urea_nitrogen: yupNullableMinZeroInteger(),
        weight_urea_nitrogen: yupNullableMinZeroInteger(),
        weight_urea_urease_inhibitor: yupNullableMinZeroInteger(),
        weight_limestone: yupNullableMinZeroInteger(),
        weight_dolomite: yupNullableMinZeroInteger(),
        area_exotic_forest_existing: yupNullableMinZeroDecimal(),
        area_exotic_forest_harvested: yupNullableMinZeroDecimal().max(
          yup.ref('area_exotic_forest_existing') as Reference<number>,
          "'Harvested this year' must be less than or equal to 'Existing'."
        ),
        area_indigenous_forest_regenerating_existing: yupNullableMinZeroDecimal(),
        area_indigenous_forest_established_existing: yupNullableMinZeroDecimal(),
        area_indigenous_forest_established_harvested: yupNullableMinZeroDecimal().max(
          yup.ref('area_indigenous_forest_established_existing') as Reference<number>,
          "'Harvested this year' must be less than or equal to 'Existing'."
        ),
        area_shrubland_lt30_existing: yupNullableMinZeroDecimal(),
        area_shrubland_gt30_existing: yupNullableMinZeroDecimal(),
        area_shrubland_gt30_harvested: yupNullableMinZeroDecimal().max(
          yup.ref('area_shrubland_gt30_existing') as Reference<number>,
          "'Harvested this year' must be less than or equal to 'Existing'."
        ),
        vegetation_existing_total: yup
          .number()
          .max(
            yup.ref('area_total') as Reference<number>,
            'Total existing vegetation plantations must be less than or equal to Total farm area.'
          ),
      } as Record<keyof FarmDetailState, any>), // eslint-disable-line @typescript-eslint/no-explicit-any
    []
  )

  const resolver = useYupValidationResolver(validationSchema)

  const formBag = useForm<FormValues>({
    mode: 'all',
    defaultValues: {
      // Default end_month for drafts created prior to this field being added
      end_month: SEASON_DEFAULT_END_MONTH,
      ...farmDetail,
      vegetation_existing_total: safeSum(EXISTING_VEGETATION_FIELDS.map((f) => farmDetail[f])),
    },
    resolver,
  })

  const { register, control, formState, handleSubmit, watch, setValue, trigger } = formBag

  useEffect(() => {
    registerForm(formBag)
    return () => registerForm(null)
  }, [formBag, registerForm])

  const region = watch('region')
  const classification = watch('classification')

  const regionAvailableFarmClasses = useMemo(() => {
    if (region) {
      return farmClasses.filter((clz) => clz.regions.includes(region))
    }
    return []
  }, [region, farmClasses])

  useEffect(() => {
    if (
      region &&
      classification &&
      !regionAvailableFarmClasses.some((clz) => clz.id === classification)
    ) {
      setValue('classification', null)
    }
  }, [region, classification, setValue, regionAvailableFarmClasses])

  // A pseudo-field for validating the sum of all the "existing vegetation"
  // fields against the "area_total"
  register('vegetation_existing_total')
  const area_total = watch('area_total')
  const season_year = watch('season_year')
  const existingVegetation = watch(EXISTING_VEGETATION_FIELDS)
  const totalExistingVegetation = safeSum(Object.values(existingVegetation))
  useEffect(() => {
    setValue('vegetation_existing_total', totalExistingVegetation)
    trigger('vegetation_existing_total')
  }, [trigger, setValue, totalExistingVegetation, area_total])

  // Don't send the pseudo-field through with the rest of the data.
  const handleSetFarmDetail = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ({ vegetation_existing_total, ...values }: FormValues) => setFarmDetail(values),
    [setFarmDetail]
  )

  // formState is wrapped with Proxy to improve render performance, so make sure you invoke or read it
  // before render in order to enable the state update.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { errors, touched } = formState

  return (
    <Form className="farm-detail">
      {formState.isSubmitted && Object.keys(errors).length > 0 && (
        <div className="mt-4">
          <span className="text-danger">Please correct the errors below to continue.</span>
        </div>
      )}
      <fieldset>
        <legend className="section-heading">Farm detail</legend>
        <Row className="row-inset">
          <Col sm={6}>
            <Form.Group as={Row} controlId="name">
              <Form.Label column sm={4}>
                Farm name
              </Form.Label>
              <Col sm={8}>
                <Form.Control
                  type="text"
                  name="name"
                  ref={register}
                  isInvalid={Boolean(errors.name)}
                  maxLength={MAX_FARM_NAME_LENGTH}
                />
                <Form.Control.Feedback type="invalid">{errors.name?.message}</Form.Control.Feedback>
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="regional_council">
              <Form.Label column sm={4}>
                Regional council
              </Form.Label>
              <Col sm={8}>
                <Controller
                  name="regional_council"
                  control={control}
                  render={({ onChange, onBlur, value, name }) => (
                    <Form.Control
                      as="select"
                      name={name}
                      isInvalid={Boolean(errors.regional_council)}
                      value={value || ''}
                      onChange={(e) => onChange(parseInt(e.target.value))}
                      onBlur={onBlur}
                    >
                      <option value="" disabled>
                        - Select a regional council -
                      </option>
                      {farmRegionalCouncils.map((iter, idx) => (
                        <option
                          value={iter.id}
                          key={idx}
                          data-testid="farm-regional-council-option"
                        >
                          {iter.name}
                        </option>
                      ))}
                    </Form.Control>
                  )}
                />

                <Form.Control.Feedback type="invalid">
                  {errors.regional_council?.message}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="region">
              <Tippy
                interactive
                content={
                  <>
                    View map in{' '}
                    <a
                      href="https://beeflambnz.com/knowledge-hub/PDF/-ghg-calculator-user-guide.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      user guide
                    </a>{' '}
                    (Appendix 1)
                  </>
                }
              >
                <Form.Label column sm={4}>
                  Production region
                  <TooltipIcon />
                </Form.Label>
              </Tippy>
              <Col sm={8}>
                <Controller
                  name="region"
                  control={control}
                  render={({ onChange, onBlur, value, name }) => (
                    <Form.Control
                      as="select"
                      name={name}
                      isInvalid={Boolean(errors.region)}
                      value={value || ''}
                      onChange={(e) => onChange(parseInt(e.target.value))}
                      onBlur={onBlur}
                    >
                      <option value="" disabled>
                        - Select a region -
                      </option>
                      {farmRegions.map((iter, idx) => (
                        <option value={iter.id} key={idx} data-testid="farm-region-option">
                          {iter.name}
                        </option>
                      ))}
                    </Form.Control>
                  )}
                />

                <Form.Control.Feedback type="invalid">
                  {errors.region?.message}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="classification">
              <Tippy
                interactive
                content={
                  <>
                    Find your farm class (or closest description of your farm) here:{' '}
                    <a href="https://beeflambnz.com/data-tools/farm-classes" target="_blank">
                      https://beeflambnz.com/data-tools/farm-classes
                    </a>
                  </>
                }
              >
                <Form.Label column sm={4}>
                  Farm class
                  <TooltipIcon />
                </Form.Label>
              </Tippy>
              <Col sm={8}>
                <Controller
                  name="classification"
                  control={control}
                  render={({ onChange, onBlur, value, name }) => (
                    <Form.Control
                      as="select"
                      name={name}
                      isInvalid={Boolean(errors.classification)}
                      value={value || ''}
                      onChange={(e) => onChange(parseInt(e.target.value))}
                      onBlur={onBlur}
                    >
                      <option value="" disabled>
                        - Select a farm class -
                      </option>
                      {regionAvailableFarmClasses.map((iter, idx) => (
                        <option value={iter.id} key={idx} data-testid="farm-classification-option">
                          {iter.name}
                        </option>
                      ))}
                    </Form.Control>
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.classification?.message}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="gst-number">
              <Tippy
                interactive
                content={<>If you have an 8-digit GST number add a 0 (zero) to the beginning</>}
              >
                <Form.Label column sm={4}>
                  Farm trading account GST number
                  <TooltipIcon />
                </Form.Label>
              </Tippy>
              <Col sm={8}>
                <Controller
                  name="gst_number"
                  control={control}
                  render={({ onChange, onBlur, value, name }) => (
                    <NumberFormat
                      id="gst-number"
                      className={classnames('form-control', {
                        'is-invalid': Boolean(errors.gst_number),
                      })}
                      mask="_"
                      format="###-###-###"
                      name={name}
                      onChange={(e) => {
                        onChange(e.currentTarget.value.replace(/[\-_]/g, ''))
                      }}
                      onBlur={onBlur}
                      value={value}
                      placeholder="000-000-000"
                    />
                  )}
                />
                GST numbers are used solely for the purpose of counting farm businesses that have
                completed this calculation. Your GST number and associated information are never
                shared with anyone
                <Form.Control.Feedback type="invalid">
                  {errors.gst_number?.message}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group as={Row} controlId="total-area">
              <Form.Label column sm={4}>
                Total farm area (ha)
              </Form.Label>
              <Col sm={8}>
                <NumberField
                  name="area_total"
                  control={control}
                  error={errors.area_total}
                  unit="ha"
                  maxLength={5}
                />
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="effective-area">
              <Form.Label column sm={4}>
                Grazed area (ha)
              </Form.Label>
              <Col sm={8}>
                <NumberField
                  name="area_effective"
                  control={control}
                  error={errors.area_effective}
                  unit="ha"
                  maxLength={5}
                />
              </Col>
            </Form.Group>

            <Form.Group as={Row} controlId="analysis-date">
              <Form.Label column sm={4}>
                Analysis date
              </Form.Label>
              <Col sm={8}>
                <InputGroup>
                  <Controller
                    name="analysis_date"
                    control={control}
                    defaultValue=""
                    render={({ onChange, onBlur, value }) => (
                      <DateInput
                        isDateOnly
                        value={value}
                        onChange={onChange}
                        defaultValues={{
                          date: '0',
                          month: '0',
                          year: '0',
                        }}
                        onBlur={onBlur}
                        wrapperClassname={errors.analysis_date ? 'is-invalid' : ''}
                      />
                    )}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.analysis_date?.message}
                  </Form.Control.Feedback>
                </InputGroup>
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="analysis-season" className="analysis-season">
              <Form.Label column sm={4}>
                Analysis season
              </Form.Label>
              <Col sm={8}>
                <NumberField
                  name="season_year"
                  control={control}
                  error={errors.season_year}
                  maxLength={4}
                />
                {season_year && !errors.season_year ? (
                  <strong className="season-end-year"> - {parseInt(season_year + '') + 1}</strong>
                ) : null}
              </Col>
            </Form.Group>
            <Form.Group as={Row} controlId="end-month" className="end-month">
              <Form.Label column sm={4}>
                Financial year end
              </Form.Label>
              <Col sm={8}>
                <Controller
                  name="end_month"
                  control={control}
                  render={({ onChange, onBlur, value, name }) => (
                    <Form.Control
                      as="select"
                      name={name}
                      isInvalid={Boolean(errors.end_month)}
                      value={value || ''}
                      onChange={(e) => onChange(parseInt(e.target.value))}
                      onBlur={onBlur}
                    >
                      <option value="" disabled>
                        - Select a month -
                      </option>
                      {MONTHS.map((month, idx) => (
                        <option value={idx + 1} key={idx} data-testid="end-month-option">
                          {month}
                        </option>
                      ))}
                    </Form.Control>
                  )}
                />

                <Form.Control.Feedback type="invalid">
                  {errors.end_month?.message}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>
          </Col>
        </Row>
      </fieldset>

      <fieldset>
        <legend className="section-heading">Fertiliser and lime use</legend>
        <Row className="row-inset">
          <Col md="10">
            <Alert variant="info">
              <p>
                This information can be found in the summary reports major fertiliser suppliers send
                out in May. Fertiliser here refers only to fertiliser containing nitrogen (N).
                Figures should be for total use (not per hectare).
              </p>
              <p>
                For fertiliser analysis please see Appendix 3 in the{' '}
                <a
                  href="https://beeflambnz.com/knowledge-hub/PDF/-ghg-calculator-user-guide.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  user guide
                </a>
              </p>
            </Alert>
          </Col>
        </Row>

        <Row className="row-inset">
          <Col>
            <Table borderless>
              <thead>
                <tr>
                  <th scope="col" className="w-col-md-4"></th>
                  <th scope="col" className="w-col-md-4">
                    Product weight (kg)
                  </th>
                  <th scope="col" className="w-col-md-4">
                    Elemental N weight (kg)
                  </th>
                </tr>
              </thead>
              <tbody>
                {([
                  ['weight_non_urea_nitrogen', 'Non-urea nitrogen fertiliser'],
                  ['weight_urea_nitrogen', 'Urea without urease inhibitor'],
                  [
                    'weight_urea_urease_inhibitor',
                    <span>
                      Urea with urease inhibitor <sup>*</sup>
                    </span>,
                  ],
                ] as const).map(([fieldName, fieldLabel], idx) => (
                  <tr key={idx}>
                    <th scope="row">{fieldLabel}</th>
                    <td></td>
                    <td>
                      <Form.Label htmlFor={fieldName} className="sr-only">
                        Elemental N weight (kg)
                      </Form.Label>
                      <NumberField
                        id={fieldName}
                        name={fieldName}
                        control={control}
                        error={errors[fieldName]}
                        unit="kg"
                        placeholder="0"
                        data-testid={fieldName}
                        maxLength={6}
                      />
                    </td>
                  </tr>
                ))}

                <tr>
                  <th scope="row">
                    <Tippy
                      interactive
                      content={
                        <>As lime and dolomite break down, they create carbon dioxide emissions</>
                      }
                    >
                      <span>
                        Lime
                        <TooltipIcon />
                      </span>
                    </Tippy>
                  </th>
                  <td>
                    <Form.Label htmlFor="weight_limestone" className="sr-only">
                      Product weight (kg)
                    </Form.Label>
                    <NumberField
                      id="weight_limestone"
                      name="weight_limestone"
                      control={control}
                      error={errors['weight_limestone']}
                      unit="kg"
                      placeholder="0"
                      data-testid="weight_limestone"
                      maxLength={7}
                    />
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <th scope="row">Dolomite</th>
                  <td>
                    <Form.Label htmlFor="weight_dolomite" className="sr-only">
                      Product weight (kg)
                    </Form.Label>
                    <NumberField
                      id="weight_dolomite"
                      name="weight_dolomite"
                      control={control}
                      error={errors['weight_dolomite']}
                      unit="kg"
                      placeholder="0"
                      data-testid="weight_dolomite"
                      maxLength={7}
                    />
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </Table>
            <p>
              <em>
                <sup>*</sup> SustainN, Agrotain, N-Protect
              </em>
            </p>
          </Col>
        </Row>
      </fieldset>

      <fieldset>
        <legend className="section-heading">Vegetation (exotic and indigenous)</legend>
        <Row className="row-inset">
          <Col md="10">
            <Alert variant="info">
              <p>
                Enter the total area of woody vegetation on your farm over 0.25ha. ‘Forest’ is able
                to reach a mature height of at least 5 metres. ‘Shrubland’ is the woody vegetation
                under 5 metres.
              </p>
              <p>This section is optional and does not have to be completed.</p>
              <p>
                These vegetation types are indicative only - this section and associated results are
                provided to give an idea of the extent of sequestration happening on farm. They do
                not reflect the eligibility for sequestration credits under the Emissions Trading
                Scheme (ETS) or any other programme.
              </p>
            </Alert>
          </Col>
        </Row>

        <Row className="row-inset">
          <Col>
            <Table borderless>
              <thead>
                <tr>
                  <th scope="col" className="w-col-md-4"></th>
                  <th scope="col" className="w-col-md-4">
                    Area at open (ha)
                  </th>
                  <th scope="col" className="w-col-md-4">
                    Harvested this year (ha)
                  </th>
                </tr>
              </thead>
              <tbody>
                {([
                  [
                    'Exotic forest (28 years or younger)',
                    'area_exotic_forest_existing',
                    'area_exotic_forest_harvested',
                  ],
                  [
                    'Indigenous forest - regenerating natural forest (less than 100 years old)',
                    'area_indigenous_forest_regenerating_existing',
                  ],
                  [
                    'Indigenous forest - established natural forest (more than 100 years old)',
                    'area_indigenous_forest_established_existing',
                    'area_indigenous_forest_established_harvested',
                  ],
                  ['Shrubland (less than 30 years old)', 'area_shrubland_lt30_existing'],
                  [
                    'Shrubland (more than 30 years old)',
                    'area_shrubland_gt30_existing',
                    'area_shrubland_gt30_harvested',
                  ],
                ] as const).map(([fieldLabel, fieldNameExisting, fieldNameHarvested], idx) => (
                  <tr key={idx}>
                    <th scope="row">{fieldLabel}</th>
                    <td>
                      <Form.Label htmlFor={fieldNameExisting} className="sr-only">
                        Existing (ha)
                      </Form.Label>
                      <NumberField
                        id={fieldNameExisting}
                        name={fieldNameExisting}
                        control={control}
                        error={errors[fieldNameExisting]}
                        unit="ha"
                        placeholder="0.0"
                        data-testid={fieldNameExisting}
                        maxLength={7}
                        numDecimals={1}
                      />
                    </td>
                    <td>
                      {fieldNameHarvested && (
                        <>
                          <Form.Label htmlFor={fieldNameHarvested} className="sr-only">
                            Harvested this year (ha)
                          </Form.Label>
                          <NumberField
                            id={fieldNameHarvested}
                            name={fieldNameHarvested}
                            control={control}
                            error={errors[fieldNameHarvested]}
                            unit="ha"
                            placeholder="0.0"
                            data-testid={fieldNameHarvested}
                            maxLength={7}
                            numDecimals={1}
                          />
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              {(formState.isSubmitted ||
                EXISTING_VEGETATION_FIELDS.some((f) => touched[f]) ||
                touched['area_total']) &&
                errors['vegetation_existing_total']?.message && (
                  <caption>
                    <Alert variant="danger">{errors['vegetation_existing_total'].message}</Alert>
                  </caption>
                )}
            </Table>
          </Col>
        </Row>
      </fieldset>

      <FormValuesWatcher control={control} onValuesChange={handleSetFarmDetail} />
      {formState.isSubmitted && Object.keys(errors).length > 0 && (
        <p className="text-right">
          <span className="text-danger">Please correct the errors above to continue.</span>
        </p>
      )}

      <GhgCalculatorNavigation handleSubmit={handleSubmit}>
        <Link
          to="/"
          className="btn btn-secondary btn-lg btn-return-to-menu mr-3"
          role="button"
          style={{ order: -1 }}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="btn-icon-left" />
          Return to menu
        </Link>
      </GhgCalculatorNavigation>
    </Form>
  )
})
